import { type CSSProperties, type ReactNode } from "react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

export type DragHandleProps = {
  ref: (el: HTMLElement | null) => void;
  attributes: Record<string, unknown>;
  listeners: Record<string, unknown> | undefined;
  style: CSSProperties;
};

interface SortableListProps<T> {
  items: T[];
  idFor: (item: T) => string;
  onReorder: (next: T[]) => void;
  renderItem: (item: T, index: number, drag: DragHandleProps) => ReactNode;
  contextId?: string;
}

export function SortableList<T>({ items, idFor, onReorder, renderItem, contextId }: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = items.map(idFor);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} id={contextId}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {items.map((item, idx) => (
          <SortableRow key={idFor(item)} id={idFor(item)}>
            {(drag) => renderItem(item, idx, drag)}
          </SortableRow>
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({ id, children }: { id: string; children: (drag: DragHandleProps) => ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, setActivatorNodeRef } = useSortable({ id });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    position: "relative",
    zIndex: isDragging ? 10 : "auto",
  };
  const drag: DragHandleProps = {
    ref: setActivatorNodeRef,
    attributes: attributes as unknown as Record<string, unknown>,
    listeners: listeners as unknown as Record<string, unknown> | undefined,
    style: {
      cursor: "grab",
      color: "var(--cc-gray-400)",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 4,
      borderRadius: 4,
      touchAction: "none",
    },
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children(drag)}
    </div>
  );
}

export function DragHandle({ drag, label = "Reordenar" }: { drag: DragHandleProps; label?: string }) {
  return (
    <span
      ref={drag.ref as unknown as (el: HTMLSpanElement | null) => void}
      {...(drag.attributes as object)}
      {...((drag.listeners ?? {}) as object)}
      style={drag.style}
      aria-label={label}
      title={label}
      data-testid="drag-handle"
    >
      <GripVertical size={14} />
    </span>
  );
}
